import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Clock, Minus, Plus, Store, Trash2, UtensilsCrossed } from "lucide-react";

/**
 * Add/edit meal form. Presentational only — every piece of state lives in
 * EventDetailPage and arrives as a prop, so opening this dialog cannot get the
 * page out of sync with it.
 *
 * `editingMealId` is what switches the dialog between "Add" and "Edit"; there
 * is no separate mode flag.
 */
export function MealDialog({
  open,
  onOpenChange,
  onSubmit,
  editingMealId,
  days,
  selectedDay,
  setSelectedDay,
  mealForm,
  setMealForm,
  MEAL_TYPES,
  vendors,
  addItemVendorId,
  setAddItemVendorId,
  addItemVendor,
  addItemVendorMenu,
  addFoodItem,
  removeFoodItem,
  updateFoodItemQty,
  mealItems,
  mealItemsTotal,
}) {
  return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogTrigger asChild>
            <Button size="sm" className="print:hidden" data-testid="add-meal-btn"><Plus className="h-4 w-4 mr-1" /> Add Meal</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-meal-dialog">
            <DialogHeader><DialogTitle className="font-heading">{editingMealId ? "Edit" : "Add"} Meal — Day {selectedDay}</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Day</Label>
                  <Select value={String(selectedDay)} onValueChange={(v) => setSelectedDay(Number(v))}>
                    <SelectTrigger data-testid="meal-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {days.map((d) => <SelectItem key={d.day_number} value={String(d.day_number)}>Day {d.day_number} - {d.day_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Meal Type</Label>
                  <Select value={mealForm.meal_type} onValueChange={(v) => setMealForm({ ...mealForm, meal_type: v })}>
                    <SelectTrigger data-testid="meal-type-select"><SelectValue placeholder="Select meal type" /></SelectTrigger>
                    <SelectContent>
                      {MEAL_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.value}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Timing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Start Time</Label>
                  <Input type="time" value={mealForm.start_time} onChange={(e) => setMealForm({ ...mealForm, start_time: e.target.value })} data-testid="meal-start-time" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> End Time</Label>
                  <Input type="time" value={mealForm.end_time} onChange={(e) => setMealForm({ ...mealForm, end_time: e.target.value })} data-testid="meal-end-time" />
                </div>
              </div>

              {/* Add Items from Vendor */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Add Items from Vendor</Label>
                <Select value={addItemVendorId} onValueChange={setAddItemVendorId}>
                  <SelectTrigger data-testid="meal-vendor-select"><SelectValue placeholder="Select a vendor to browse menu" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a vendor...</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {addItemVendor && addItemVendorMenu.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border max-h-44 overflow-y-auto" data-testid="vendor-menu-items-section">
                  {addItemVendorMenu.map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-primary/5 transition-colors"
                      onClick={() => addFoodItem(item)}
                      data-testid={`vendor-item-${item.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{item.name}</p>
                        {item.category && <p className="text-[10px] text-muted-foreground">{item.category}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {item.price != null && (
                          <span className="text-sm font-semibold text-emerald-600">${Number(item.price).toFixed(2)}</span>
                        )}
                        <Plus className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {addItemVendor && addItemVendorMenu.length === 0 && (
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">This vendor has no menu items.</p>
              )}

              {/* Selected Items List */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><UtensilsCrossed className="h-3.5 w-3.5" /> Food Items ({mealItems.length})</Label>
                {mealItems.length > 0 ? (
                  <div className="rounded-lg border border-border divide-y divide-border" data-testid="meal-items-list">
                    {mealItems.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2.5 text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{item.name}</p>
                          <p className="text-[10px] text-muted-foreground">{item.vendor_name}{item.category ? ` · ${item.category}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="flex items-center gap-1 bg-muted rounded-md">
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateFoodItemQty(idx, -1)} data-testid={`qty-minus-${idx}`}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-xs font-medium w-6 text-center" data-testid={`qty-value-${idx}`}>{item.quantity}</span>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateFoodItemQty(idx, 1)} data-testid={`qty-plus-${idx}`}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <span className="text-xs text-muted-foreground w-12 text-right">${Number(item.price).toFixed(2)}</span>
                          <span className="text-sm font-semibold text-emerald-600 w-16 text-right" data-testid={`item-subtotal-${idx}`}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeFoodItem(idx)} data-testid={`remove-item-${idx}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-muted/40">
                      <span className="text-sm font-medium text-foreground">Total</span>
                      <span className="text-sm font-bold text-emerald-600" data-testid="meal-dialog-total">${mealItemsTotal.toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg text-center">No items yet. Select a vendor above and click items to add them.</p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes / Special Instructions</Label>
                <Textarea placeholder="No nuts, extra spicy, dietary requirements..." value={mealForm.notes} onChange={(e) => setMealForm({ ...mealForm, notes: e.target.value })} rows={2} data-testid="meal-notes-input" />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-meal-btn">{editingMealId ? "Update Meal" : "Add Meal"}</Button>
            </form>
          </DialogContent>
        </Dialog>
  );
}

export default MealDialog;
